import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { getUiLanguage } from '@/shared/config';
import { formatDateTime } from '@/shared/lib';
import { CardBlock } from './CardBlock';

export interface HistoryEntry {
  key: string;
  /** Момент события, ISO 8601. */
  at: string;
  /** «Цена изменена» */
  event: ReactNode;
  /** «2 400 ₽ → 2 550 ₽ · Ирина Полякова» */
  details?: ReactNode;
}

export interface HistoryBlockProps {
  title?: ReactNode;
  entries: readonly HistoryEntry[];
}

/**
 * История (прототип карточки): таблица «Дата | Событие | Детали» 148px / 1fr / 1.6fr, только чтение.
 * Общий примитив (как `CardBlock`): им пользуются виджет карточки и слайсы ниже виджетов (история
 * записи каталога в `entities/catalog`).
 */
export function HistoryBlock({ title, entries }: HistoryBlockProps) {
  const { t, i18n } = useTranslation('card');
  const language = i18n.language || getUiLanguage();
  return (
    <CardBlock title={title ?? t('history.title')} note={t('history.note')}>
      <div className="overflow-x-auto">
        <div className="min-w-640">
          <div className="grid h-32 grid-cols-[148px_minmax(200px,1fr)_minmax(240px,1.6fr)] items-stretch border-b border-line-hair bg-surface-sunken">
            <span className="flex items-center border-r border-line-hair px-12 text-[10px] font-semibold tracking-[0.04em] text-ink-600 uppercase">
              {t('history.date')}
            </span>
            <span className="flex items-center border-r border-line-hair px-10 text-[10px] font-semibold tracking-[0.04em] text-ink-600 uppercase">
              {t('history.event')}
            </span>
            <span className="flex items-center px-10 text-[10px] font-semibold tracking-[0.04em] text-ink-600 uppercase">
              {t('history.details')}
            </span>
          </div>
          {entries.length === 0 ? (
            <p className="px-14 py-12 text-[12px] text-ink-400">{t('history.empty')}</p>
          ) : (
            <ul>
              {entries.map((entry) => (
                <li
                  key={entry.key}
                  className="grid grid-cols-[148px_minmax(200px,1fr)_minmax(240px,1.6fr)] items-stretch border-b border-line-soft last:border-b-0"
                >
                  <time
                    dateTime={entry.at}
                    className="border-r border-line-soft px-14 py-9 text-[12px] whitespace-nowrap text-ink-500 tabular"
                  >
                    {formatDateTime(entry.at, language)}
                  </time>
                  <span className="border-r border-line-soft px-12 py-9 text-[12.5px] leading-[18px] font-medium text-pretty text-ink-900">
                    {entry.event}
                  </span>
                  <span className="px-12 py-9 text-[12.5px] leading-[18px] text-pretty text-ink-600">
                    {entry.details}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </CardBlock>
  );
}
